import React, { useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Users, Maximize2, Minimize2, AlertTriangle } from 'lucide-react';
import CarerRow from './CarerRow';
import { PX_PER_HOUR, hoursOf, computeHourWidths, makeXScale } from './scheduleUtils';
import { todayKey } from '../../../utils/datetime';

// The single hour ruler shared by EVERY zone in the board (one common
// timeline, not one per zone). Sticky to the top of the scroll pane so it
// stays visible while the carer rows scroll vertically; its left cell is
// sticky too so the ZONE / CARER column never slides under it.
const HourRuler = ({ hours, hourWidths, fullscreen, onToggleFullscreen, onScrollLeft, onScrollRight, nowFrac, nowLabel }) => (
  <div className="sticky top-0 z-30 flex min-h-[52px] border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#20242c]">
    <div className="sticky left-0 z-40 flex w-[180px] flex-shrink-0 items-center justify-between gap-1 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#20242c] px-2 py-2">
      <div className="flex min-w-0 items-center gap-1.5">
        <button onClick={onToggleFullscreen} title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'} className="grid h-6 w-6 flex-shrink-0 place-items-center rounded-md border border-blue-200 dark:border-blue-900/50 bg-white dark:bg-[#31373f] text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20">
          {fullscreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </button>
        <span className="truncate text-[10px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500">Zone / Carer</span>
      </div>
      <div className="flex flex-shrink-0 items-center gap-1">
        <button onClick={onScrollLeft} title="Scroll timeline left" className="grid h-6 w-6 place-items-center rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f]">
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button onClick={onScrollRight} title="Scroll timeline right" className="grid h-6 w-6 place-items-center rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#31373f]">
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
    <div className="relative flex-1">
      <div className="flex h-full">
        {hours.slice(0, -1).map((h, i) => (
          <div key={h} style={{ flexGrow: hourWidths[i] || PX_PER_HOUR, flexBasis: 0 }} className="border-r border-gray-100 dark:border-gray-800 pt-2 pl-1.5 text-left text-[10px] font-semibold text-gray-400 dark:text-gray-500">
            {String(h).padStart(2, '0')}:00
          </div>
        ))}
      </div>
      {/* Current-time label + dot, rendered INSIDE the ruler's timeline area so
          the sticky ZONE/CARER cell (to its left) covers it when scrolled left.
          Sits in the ruler (which is sticky-top), so it stays visible on scroll.
          The dot straddles the ruler's bottom border (the "now" line's top). */}
      {nowFrac != null && (
        <div className="pointer-events-none absolute bottom-1 z-20 flex -translate-x-1/2 flex-col items-center" style={{ left: `${nowFrac * 100}%` }}>
          <span className="text-[10px] font-bold leading-none text-teal-600 dark:text-teal-400 whitespace-nowrap">{nowLabel}</span>
          <span className="mt-0.5 h-3 w-3 rounded-full bg-teal-500 shadow ring-2 ring-white dark:ring-[#20242c] animate-pulse" />
        </div>
      )}
    </div>
  </div>
);

// One zone = an accordion section: a full-width header bar (collapsible) then
// its carer rows. All sections share the board's single timeline, so the hour
// columns line up across zones.
const ZoneAccordion = ({ zone, geo, hourWidths, onVisitClick, onEditZone, onLeaveClick, onReview, reviewCount = 0, onCarerDetails, onReviewVisit, onShiftClick, dragActive, dragVisit, onDropVisit }) => {
  // Zones with no carers start collapsed (nothing to show until staff join).
  const [collapsed, setCollapsed] = useState(zone.carers.length === 0);
  const visitCount = zone.carers.reduce((n, c) => n + (c.visits?.length || 0), 0);

  return (
    <div className="border-t border-gray-100 dark:border-gray-800 first:border-t-0">
      {/* Zone header bar — all controls grouped on the left and pinned there so
          they stay visible while the timeline scrolls horizontally. */}
      <div className="border-b border-gray-100 dark:border-gray-800 bg-[#F7F9FC] dark:bg-[#20242c]">
        <div className="sticky left-0 z-[28] flex w-max max-w-full items-center gap-3 bg-[#F7F9FC] dark:bg-[#20242c] px-4 py-2.5">
          <button onClick={() => setCollapsed((c) => !c)} className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]">
            <ChevronDown className={`h-4 w-4 transition ${collapsed ? '-rotate-90' : ''}`} />
          </button>
          <span className="h-3 w-3 flex-shrink-0 rounded-full" style={{ background: zone.color || '#2563eb' }} />
          <div className="mr-2">
            <div className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-200">
              {zone.name}
              {/* Travel area (postcodes) shown inline right after the zone name. */}
              <span className="ml-2 text-[11px] font-semibold normal-case tracking-normal text-gray-400 dark:text-gray-500">
                · {zone.postcode_prefixes?.length ? zone.postcode_prefixes.join(', ') : 'No area set'}
              </span>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 dark:bg-[#31373f] px-2 py-1 text-[11px] font-semibold text-gray-500 dark:text-gray-400"><Users className="h-3 w-3" />{zone.carers.length} roster lines</span>
          <span className="rounded-md bg-gray-100 dark:bg-[#31373f] px-2 py-1 text-[11px] font-semibold text-gray-500 dark:text-gray-400">{visitCount} package visits</span>
          {reviewCount > 0 && onReview && (
            <button
              onClick={() => onReview(zone)}
              className="inline-flex items-center gap-1 rounded-md border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/20 px-2 py-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/40"
              title={`${reviewCount} item(s) need review`}
            >
              <AlertTriangle className="h-3 w-3" /> Review {reviewCount}
            </button>
          )}
          {zone.id != null && onEditZone && (
            <button onClick={() => onEditZone(zone)} className="rounded-md border border-blue-200 dark:border-blue-900/50 px-2.5 py-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20">Edit Zone</button>
          )}
        </div>
      </div>

      {!collapsed && (
        zone.carers.length === 0 ? (
          <div className="px-4 py-6 text-center text-sm text-gray-400 dark:text-gray-500">No carers assigned to this zone yet.</div>
        ) : (
          zone.carers.map((carer) => (
            <CarerRow
              key={carer.id}
              carer={carer}
              geo={geo}
              hourWidths={hourWidths}
              onVisitClick={onVisitClick}
              onLeaveClick={onLeaveClick}
              onCarerDetails={onCarerDetails}
              onReviewVisit={onReviewVisit ? (c, v) => onReviewVisit(zone, c, v) : undefined}
              onShiftClick={onShiftClick}
              dragActive={dragActive}
              dragVisit={dragVisit}
              onDropVisit={onDropVisit}
            />
          ))
        )
      )}
    </div>
  );
};

const ZoneRoster = ({ zones = [], date, win, unassignedSlot, dateLabel, onPrevDay, onNextDay, onToday, isTodaySelected, onVisitClick, onEditZone, onLeaveClick, onReview, reviewCountOf, onCarerDetails, onReviewVisit, onShiftClick, dragActive, dragVisit, onDropVisit }) => {
  const [fullscreen, setFullscreen] = useState(false);
  const scrollRef = useRef(null);
  const hours = hoursOf(win);
  // Non-uniform hour widths: short busy shifts widen the hours they span so the
  // visit cards fit, and the whole board shares this one scale (ruler, gridlines,
  // bands, markers) so everything stays aligned.
  const hourWidths = useMemo(() => computeHourWidths(win, zones), [win, zones]);
  const scale = useMemo(() => makeXScale(win, hourWidths), [win, hourWidths]);
  const boardWidth = 180 + scale.totalWidth;

  // "Now" reference line — a full-height marker at the CURRENT time (only when
  // viewing today and the time falls inside the visible window). `nowFrac` is a
  // 0..1 fraction of the timeline content used to place the line over the
  // hour columns (which sit to the right of the 180px Zone/Carer column).
  const isToday = date === todayKey();
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const nowInRange = isToday && nowMins >= win.startHour * 60 && nowMins <= win.endHour * 60;
  const nowFrac = nowInRange ? scale.xOf(nowMins) / scale.totalWidth : null;
  const nowLabel = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  // Step the timeline left/right with the header arrows (≈ 3 hours per click)
  // so the manager can pan without dragging.
  const scrollByX = (dir) => scrollRef.current?.scrollBy({ left: dir * PX_PER_HOUR * 3, behavior: 'smooth' });

  if (zones.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {unassignedSlot}
        <div className="rounded-2xl border border-dashed border-gray-200 dark:border-gray-800 bg-white dark:bg-[#262b33] px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
          No zones yet. Use <b className="text-gray-600 dark:text-gray-400">Create zone</b> to group carers by travel area.
        </div>
      </div>
    );
  }

  const board = (
    <>
      {/* 2-axis scroll pane — the timeline scrolls INSIDE the zone. The hour
          ruler (sticky top) and the ZONE/CARER column (sticky left) stay frozen
          while the carer rows scroll. */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto">
        <div className="relative" style={{ minWidth: boardWidth }}>
          {/* "Now" marker — a full-height line at the current time so the
              manager can see at a glance which visits are past vs upcoming. */}
          {/* Full-height "now" line. z-[25] sits ABOVE the grid bands/visit
              cards but BELOW the sticky ZONE/CARER columns (bumped to z-[28])
              so it slips under the carer names when scrolled left — and below
              the hour ruler (z-30). The time label + dot live inside the ruler
              (see below) so they stay pinned at the top. */}
          {nowFrac != null && (
            <div
              className="pointer-events-none absolute top-0 bottom-0 z-[25] w-px -translate-x-1/2 bg-teal-500"
              style={{ left: `calc(180px + (100% - 180px) * ${nowFrac})` }}
            />
          )}
          <HourRuler
            hours={hours}
            hourWidths={hourWidths}
            fullscreen={fullscreen}
            onToggleFullscreen={() => setFullscreen((f) => !f)}
            onScrollLeft={() => scrollByX(-1)}
            onScrollRight={() => scrollByX(1)}
            nowFrac={nowFrac}
            nowLabel={nowLabel}
          />
          {zones.map((zone) => (
            <ZoneAccordion
              key={zone.id ?? 'unzoned'}
              zone={zone}
              geo={scale.blockGeo}
              hourWidths={hourWidths}
              onVisitClick={onVisitClick}
              onEditZone={onEditZone}
              onLeaveClick={onLeaveClick}
              onReview={onReview}
              reviewCount={reviewCountOf ? reviewCountOf(zone) : 0}
              onCarerDetails={onCarerDetails}
              onReviewVisit={onReviewVisit}
              onShiftClick={onShiftClick}
              dragActive={dragActive}
              dragVisit={dragVisit}
              onDropVisit={onDropVisit}
            />
          ))}
        </div>
      </div>
    </>
  );

  if (fullscreen) {
    return (
      <div className="fixed inset-0 z-[70] flex flex-col gap-3 bg-[#EEF2F6] p-3 dark:bg-[#1b1f26]">
        {/* Compact fullscreen bar — date nav + back to schedule (no Daily
            schedule card here). */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-l-4 border-gray-100 dark:border-gray-800 border-l-rose-400 bg-white dark:bg-[#262b33] px-4 py-3 shadow-sm">
          <div className="flex items-baseline gap-2">
            <span className="text-[15px] font-bold text-gray-800 dark:text-gray-200">Schedule view</span>
            <span className="text-[13px] font-medium text-gray-400 dark:text-gray-500">{dateLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onPrevDay} title="Previous day" className="grid h-8 w-8 place-items-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-1 text-[13px] font-semibold text-gray-700 dark:text-gray-200">{dateLabel}</span>
            <button onClick={onNextDay} title="Next day" className="grid h-8 w-8 place-items-center rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]">
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={onToday}
              disabled={isTodaySelected}
              title="Jump to today"
              className={`ml-1 inline-flex items-center rounded-lg px-3 py-1.5 text-[13px] font-bold transition ${
                isTodaySelected
                  ? 'border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#31373f] text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'border border-[#2563EB] bg-white dark:bg-[#31373f] text-[#2563EB] dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'
              }`}
            >
              Today
            </button>
            <button onClick={() => setFullscreen(false)} className="ml-2 inline-flex items-center gap-1.5 rounded-lg border border-blue-200 dark:border-blue-900/50 px-3 py-1.5 text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20">
              <Minimize2 className="h-3.5 w-3.5" /> Back to schedule
            </button>
          </div>
        </div>
        {unassignedSlot}
        <div className="flex flex-1 flex-col overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#262b33] shadow-sm">{board}</div>
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {unassignedSlot}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-[#262b33] shadow-sm">{board}</div>
    </div>
  );
};

export default ZoneRoster;
